import React, { useState, useEffect, useMemo, useCallback } from "react";
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Stethoscope, 
  Search, 
  ChevronRight, 
  Heart,
  X,
  Activity,
  FileText,
  Database,
  AlertCircle,
  RefreshCw,
  Zap
} from 'lucide-react';

// Import our new components and utilities
import { ThemeProvider } from './contexts/ThemeContext';
import LoadingSpinner from './components/ui/LoadingSpinner';
import Button from './components/ui/Button';
import SearchBar from './components/ui/SearchBar';
import ThemeToggle from './components/ui/ThemeToggle';
import DataTable from './components/ui/DataTable';
import VirtualizedTable from './components/ui/VirtualizedTable';
import { useDebounce } from './hooks/useDebounce';
import { 
  initializeSearch, 
  performSearch, 
  getSearchSuggestions 
} from './utils/searchEngine';
import "./index.css";

// For production deployment, we'll use static data only
const API = process.env.REACT_APP_API_URL || "http://127.0.0.1:8000";
const USE_STATIC_DATA = !process.env.REACT_APP_API_URL;

// Helper: does this node or any descendant have codes?
function hasAnyCodes(node) {
  if ((node.codes || []).length > 0) return true;
  return (node.children || []).some(hasAnyCodes);
}

// Helper: count codes in a node including descendants (for accurate badges)
function countCodes(node) {
  const selfCount = (node.codes || []).length;
  const childCount = (node.children || []).reduce((sum, c) => sum + countCodes(c), 0);
  return selfCount + childCount;
}

// Memoized Folder Component for better performance
const OptimizedFolder = React.memo(({ 
  node, 
  level = 0, 
  openGroups, 
  toggleGroup, 
  dataMap, 
  columns, 
  onFavorite, 
  favorites, 
  useVirtualization = false 
}) => {
  const isOpen = Boolean(openGroups[node.label]);
  
  return (
    <motion.div 
      className="mb-4" 
      style={{ marginLeft: level * 16 }}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: level * 0.1 }}
    >
      <motion.div
        className="flex items-center justify-between bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-gray-700 dark:to-gray-600 hover:from-blue-100 hover:to-indigo-100 dark:hover:from-gray-600 dark:hover:to-gray-500 rounded-xl p-4 shadow-sm cursor-pointer border border-blue-100 dark:border-gray-600 transition-all duration-200"
        onClick={() => toggleGroup(node.label)}
        whileHover={{ scale: 1.01, y: -2 }}
        whileTap={{ scale: 0.99 }}
      >
        <div className="flex items-center space-x-3">
          <motion.div
            animate={{ rotate: isOpen ? 90 : 0 }}
            transition={{ duration: 0.2 }}
          >
            <ChevronRight className="h-5 w-5 text-blue-600 dark:text-blue-400" />
          </motion.div>
          <div className="flex items-center space-x-2">
            <Database className="h-5 w-5 text-blue-500 dark:text-blue-400" />
            <span className="font-semibold text-lg text-gray-800 dark:text-white">
              {node.label}
            </span>
          </div>
        </div>
        <div className="flex items-center space-x-2 text-sm text-gray-500 dark:text-gray-400">
          <span>{countCodes(node)} codes</span>
          {useVirtualization && node.codes && node.codes.length > 100 && (
            <Zap className="h-4 w-4 text-yellow-500" title="Virtualized for performance" />
          )}
        </div>
      </motion.div>
      
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
            className="mt-4 overflow-hidden"
          >
            {node.children
              .filter(hasAnyCodes)
              .map((child) => (
                <OptimizedFolder
                  key={child.label}
                  node={child}
                  level={level + 1}
                  openGroups={openGroups}
                  toggleGroup={toggleGroup}
                  dataMap={dataMap}
                  columns={columns}
                  onFavorite={onFavorite}
                  favorites={favorites}
                  useVirtualization={useVirtualization}
                />
              ))}
            {node.codes && node.codes.length > 0 && (
              <div className="mt-4">
                <DataTable 
                  codes={node.codes} 
                  dataMap={dataMap} 
                  columns={columns}
                  onFavorite={onFavorite}
                  favorites={favorites}
                />
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
});

// Main App Component with Performance Optimizations
function MedCodeHubApp() {
  const [view, setView] = useState("cpt");
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [openGroups, setOpenGroups] = useState({});
  const [favorites, setFavorites] = useState([]);
  const [recentSearches, setRecentSearches] = useState([]);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  
  // Loading and error states
  const [isSearching, setIsSearching] = useState(false);
  const [isLoadingData, setIsLoadingData] = useState(false);
  const [error, setError] = useState(null);
  
  // Performance optimization states
  const [useVirtualization, setUseVirtualization] = useState(false);

  const [cptFolders, setCptFolders] = useState(null);
  const [cptDataMap, setCptDataMap] = useState({});
  const [icdFolders, setIcdFolders] = useState(null);
  const [icdDataMap, setIcdDataMap] = useState({});

  // Debounce search query for performance
  const debouncedQuery = useDebounce(query, 300);

  // Column definitions
  const CPT_COLUMNS = [
    { key: "code", label: "Code" },
    { key: "description", label: "Description" },
    { key: "work_rvu", label: "Work RVU" },
    { key: "global_period", label: "Global Period" },
    { key: "Hospital_Medicare_Payment", label: "Hospital Payment" },
    { key: "ASC_Medicare_Payment", label: "ASC Payment" },
    { key: "Hospital_Copay", label: "Hospital Copay" },
    { key: "ASC_Copay", label: "ASC Copay" },
  ];

  const ICD_COLUMNS = [
    { key: "code", label: "Code" },
    { key: "description", label: "Description" },
  ];

  // Load favorites from localStorage
  useEffect(() => {
    const savedFavorites = localStorage.getItem('medcodehub-favorites');
    const savedSearches = localStorage.getItem('medcodehub-recent-searches');
    
    if (savedFavorites) {
      setFavorites(JSON.parse(savedFavorites));
    }
    if (savedSearches) {
      setRecentSearches(JSON.parse(savedSearches));
    }
  }, []);

  // Save favorites to localStorage
  const saveFavorites = useCallback((newFavorites) => {
    setFavorites(newFavorites);
    localStorage.setItem('medcodehub-favorites', JSON.stringify(newFavorites));
  }, []);

  // Handle favorite toggle
  const handleFavorite = useCallback((code) => {
    const newFavorites = favorites.includes(code)
      ? favorites.filter(f => f !== code)
      : [...favorites, code];
    saveFavorites(newFavorites);
  }, [favorites, saveFavorites]);

  // Memoized data processing
  const processedData = useMemo(() => {
    const dataMap = view === "cpt" ? cptDataMap : icdDataMap;
    const dataArray = Object.values(dataMap);
    
    // Initialize search engine when data changes
    if (dataArray.length > 0) {
      initializeSearch(view, dataArray);
      
      // Enable virtualization for large datasets
      setUseVirtualization(dataArray.length > 1000);
    }
    
    return { dataMap, dataArray };
  }, [view, cptDataMap, icdDataMap]);

  // Optimized search function with fuzzy matching
  const handleSearch = useCallback(async () => {
    if (!debouncedQuery.trim()) {
      setResults([]);
      return;
    }
    
    setIsSearching(true);
    setError(null);

    // Add to recent searches
    const newRecentSearches = [debouncedQuery, ...recentSearches.filter(s => s !== debouncedQuery)].slice(0, 5);
    setRecentSearches(newRecentSearches);
    localStorage.setItem('medcodehub-recent-searches', JSON.stringify(newRecentSearches));

    try {
      // Use fuzzy search engine for better results
      const searchResults = performSearch(view, debouncedQuery, {
        limit: useVirtualization ? 1000 : 200
      });
      
      setResults(searchResults);
      setOpenGroups({});
      
    } catch (error) {
      // Fallback to basic search
      const { dataArray } = processedData;
      const fallbackResults = dataArray.filter(item => 
        item.code.toLowerCase().includes(debouncedQuery.toLowerCase()) ||
        item.description.toLowerCase().includes(debouncedQuery.toLowerCase())
      ).slice(0, 200);
      
      setResults(fallbackResults);
      setOpenGroups({});
    } finally {
      setIsSearching(false);
    }
  }, [debouncedQuery, view, recentSearches, processedData, useVirtualization]);

  // Generate smart suggestions
  const generateSuggestions = useCallback((searchType, searchQuery) => {
    return getSearchSuggestions(searchType, searchQuery, 8);
  }, []);

  // Auto-search when debounced query changes
  useEffect(() => {
    if (debouncedQuery.trim()) {
      handleSearch();
    }
  }, [debouncedQuery, handleSearch]);

  // Manual search trigger
  const triggerSearch = useCallback(() => {
    if (query.trim()) {
      handleSearch();
    }
  }, [query, handleSearch]);

  // Load CPT data (robust: always use folders; API optional enhancement only)
  useEffect(() => {
    if (view === "cpt") {
      setQuery("");
      setResults([]);
      setOpenGroups({});
      setError(null);
      setIsLoadingData(true);

      // Always load folders and build a baseline map
      fetch("/cpt_folders_nested.json")
        .then(r => {
          if (!r.ok) throw new Error(`Failed to load CPT folders: ${r.status}`);
          return r.json();
        })
        .then(data => {
          setCptFolders(data);
          const map = {};
          const extractCodes = (node) => {
            (node.codes || []).forEach(code => { if (code && code.code) map[code.code] = code; });
            (node.children || []).forEach(extractCodes);
          };
          data.forEach(extractCodes);
          setCptDataMap(map);
          setIsLoadingData(false);

          // Optionally try to enhance with API data without blocking UI
          if (process.env.REACT_APP_API_URL) {
            fetch(`${API}/api/cpt?search=`)
              .then(r => r.ok ? r.json() : Promise.reject(new Error(`CPT API ${r.status}`)))
              .then(apiData => {
                if (Array.isArray(apiData) && apiData.length > 0) {
                  const apiMap = { ...map };
                  apiData.forEach(item => { if (item && item.code) apiMap[item.code] = { ...apiMap[item.code], ...item }; });
                  setCptDataMap(apiMap);
                }
              })
              .catch(() => { /* non-fatal; keep folder data */ });
          }
        })
        .catch(() => {
          setError("Failed to load CPT folder structure");
          setIsLoadingData(false);
        });
    }
  }, [view]);

  // Load ICD data
  useEffect(() => {
    if (view === "icd") {
      setIcdFolders(null);
      setQuery("");
      setResults([]);
      setOpenGroups({});
      setError(null);
      setIsLoadingData(true);

      fetch("/icd10_folders_nested.json")
        .then((r) => {
          if (!r.ok) throw new Error(`Failed to load ICD-10 folders: ${r.status}`);
          return r.json();
        })
        .then((data) => {
          // Build hierarchical folders directly from provided ICD-10 nested JSON:
          // Chapters (top-level labels) -> Blocks (second-level labels) -> Codes table

          // Map of code -> { code, description }
          const codeMap = {};

          // Collect all codes under a node into an array and fill codeMap
          const collectCodes = (node, arr) => {
            if (node && node.code && node.description) {
              const item = { code: String(node.code), description: node.description };
              codeMap[item.code] = item;
              arr.push(item);
            }
            (node.children || []).forEach((child) => collectCodes(child, arr));
          };

          // Transform: keep original chapters/blocks, aggregate codes at block level
          const folders = (data || []).map((chapter) => {
            const children = (chapter.children || []).map((block) => {
              const codes = [];
              (block.children || []).forEach((child) => collectCodes(child, codes));
              // Sort codes in each block for a nicer UX
              codes.sort((a, b) => a.code.localeCompare(b.code));
              return {
                label: block.label || block.name || "Block",
                codes,
                children: []
              };
            }).filter(hasAnyCodes);

            return {
              label: chapter.label || chapter.name || "Chapter",
              codes: [],
              children: children
            };
          }).filter(hasAnyCodes);

          setIcdFolders(folders);
          setIcdDataMap(codeMap);
          setIsLoadingData(false);
        })
        .catch(err => {
          setError("Failed to load ICD-10 data");
          setIsLoadingData(false);
        });
    }
  }, [view]);

  const toggleGroup = useCallback((label) =>
    setOpenGroups((p) => ({ ...p, [label]: !p[label] })), []);

  // Render content
  const isCpt = view === "cpt";
  const folders = isCpt ? cptFolders : icdFolders;
  const dataMap = isCpt ? cptDataMap : icdDataMap;
  const columns = isCpt ? CPT_COLUMNS : ICD_COLUMNS;

  let content;
  if (error) {
    content = (
      <motion.div 
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl p-6 text-center"
      >
        <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
        <div className="text-red-600 dark:text-red-400 font-medium mb-2 text-lg">System Error</div>
        <div className="text-red-700 dark:text-red-300 mb-4">{error}</div>
        <Button
          onClick={() => {
            setError(null);
            window.location.reload();
          }}
          variant="danger"
          icon={<RefreshCw className="h-4 w-4" />}
        >
          Retry Connection
        </Button>
      </motion.div>
    );
  } else if (isLoadingData) {
    content = (
      <LoadingSpinner 
        size="lg" 
        text={`Loading ${isCpt ? "CPT" : "ICD-10"} medical codes...`} 
      />
    );
  } else if (query.trim()) {
    if (isSearching) {
      content = <LoadingSpinner size="lg" text="Searching medical codes with AI..." />;
    } else if (results.length === 0) {
      content = (
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center py-12 text-gray-500 dark:text-gray-400"
        >
          <Search className="h-16 w-16 mx-auto mb-4 text-gray-300 dark:text-gray-600" />
          <p className="text-xl font-medium mb-2">No results found</p>
          <p className="text-sm">No medical codes found for "{query}"</p>
          <p className="text-sm mt-2">Try different keywords or check your spelling</p>
        </motion.div>
      );
    } else {
      content = (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <div className="mb-6 flex items-center justify-between">
            <div className="text-sm text-gray-600 dark:text-gray-400 flex items-center space-x-2">
              <span>Found <span className="font-semibold text-blue-600 dark:text-blue-400">{results.length}</span> result{results.length !== 1 ? 's' : ''} for "{query}"</span>
              {useVirtualization && (
                <div className="flex items-center space-x-1 text-yellow-600 dark:text-yellow-400">
                  <Zap className="h-4 w-4" />
                  <span className="text-xs">Optimized</span>
                </div>
              )}
            </div>
          </div>
          <DataTable 
            codes={results} 
            dataMap={dataMap} 
            columns={columns}
            onFavorite={handleFavorite}
            favorites={favorites}
          />
        </motion.div>
      );
    }
  } else if (folders === null) {
    content = (
      <LoadingSpinner 
        size="lg" 
        text={`Loading ${isCpt ? "CPT" : "ICD-10"} browse structure...`} 
      />
    );
  } else {
    content = (
      <motion.div 
        className="space-y-4"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ staggerChildren: 0.1 }}
      >
        {folders.filter(hasAnyCodes).map((group) => (
          <OptimizedFolder
            key={group.label}
            node={group}
            openGroups={openGroups}
            toggleGroup={toggleGroup}
            dataMap={dataMap}
            columns={columns}
            onFavorite={handleFavorite}
            favorites={favorites}
            useVirtualization={useVirtualization}
          />
        ))}
      </motion.div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50 dark:from-gray-900 dark:to-gray-800 transition-colors duration-300">
      {/* Header */}
      <motion.header 
        className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-lg border-b border-gray-200 dark:border-gray-700 sticky top-0 z-50"
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Logo and Title */}
            <motion.div 
              className="flex items-center space-x-3"
              whileHover={{ scale: 1.05 }}
            >
              <div className="flex items-center justify-center w-10 h-10 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-xl">
                <Stethoscope className="h-6 w-6 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">
                  MedCodeHub
                </h1>
                <p className="text-xs text-gray-500 dark:text-gray-400">Professional Medical Coding Platform</p>
              </div>
            </motion.div>

            {/* Header Actions */}
            <div className="flex items-center space-x-4">
              <div className="hidden md:flex items-center space-x-2 text-sm text-gray-600 dark:text-gray-300">
                <Activity className="h-4 w-4" />
                <span>Live Data</span>
                {useVirtualization && (
                  <>
                    <Zap className="h-4 w-4 text-yellow-500" />
                    <span className="text-yellow-600 dark:text-yellow-400">Optimized</span>
                  </>
                )}
              </div>
              <ThemeToggle />
              <Button
                variant="ghost"
                size="sm"
                icon={<Heart className="h-4 w-4" />}
                onClick={() => setSidebarOpen(!sidebarOpen)}
              >
                Favorites ({favorites.length})
              </Button>
            </div>
          </div>
        </div>
      </motion.header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Navigation Tabs */}
        <motion.div 
          className="flex justify-center mb-8"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >
          <div className="bg-white dark:bg-gray-800 rounded-xl p-1 shadow-lg border border-gray-200 dark:border-gray-700">
            <div className="flex space-x-1">
              <Button
                onClick={() => setView("cpt")}
                disabled={isLoadingData}
                variant={isCpt ? "primary" : "ghost"}
                icon={<FileText className="h-4 w-4" />}
              >
                CPT Explorer
              </Button>
              <Button
                onClick={() => setView("icd")}
                disabled={isLoadingData}
                variant={!isCpt ? "primary" : "ghost"}
                icon={<Database className="h-4 w-4" />}
              >
                ICD-10 Browser
              </Button>
            </div>
          </div>
        </motion.div>

        {/* Search Section */}
        <motion.div 
          className="mb-8"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
        >
          <SearchBar
            value={query}
            onChange={setQuery}
            onSearch={triggerSearch}
            placeholder={isCpt ? "Search CPT codes and procedures..." : "Search ICD-10 diagnosis codes..."}
            recentSearches={recentSearches}
            isLoading={isSearching}
            disabled={isLoadingData}
            searchType={view}
            onSuggestionGenerate={generateSuggestions}
          />
        </motion.div>

        {/* Content Area */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
        >
          {content}
        </motion.div>
      </main>

      {/* Sidebar for Favorites */}
      <AnimatePresence>
        {sidebarOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/50 z-40"
              onClick={() => setSidebarOpen(false)}
            />
            <motion.div
              initial={{ x: 300 }}
              animate={{ x: 0 }}
              exit={{ x: 300 }}
              className="fixed right-0 top-0 h-full w-80 bg-white dark:bg-gray-800 shadow-xl z-50 overflow-y-auto"
            >
              <div className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Favorites</h2>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setSidebarOpen(false)}
                    icon={<X className="h-4 w-4" />}
                  />
                </div>
                {favorites.length === 0 ? (
                  <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                    <Heart className="h-12 w-12 mx-auto mb-4 text-gray-300 dark:text-gray-600" />
                    <p>No favorites yet</p>
                    <p className="text-sm mt-2">Click the heart icon on any code to add it here</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {favorites.map((code) => (
                      <div
                        key={code}
                        className="p-3 bg-gray-50 dark:bg-gray-700 rounded-lg flex items-center justify-between"
                      >
                        <span className="font-mono text-sm">{code}</span>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleFavorite(code)}
                          icon={<X className="h-3 w-3" />}
                        />
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
}

// Wrap with ThemeProvider
export default function App() {
  return (
    <ThemeProvider>
      <MedCodeHubApp />
    </ThemeProvider>
  );
}
